import {
  Box,
  Typography,
  TextField as MUITextField,
  FormHelperText,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import {
  isNotBlank,
  statesAbbreviation,
  statesNames,
} from '@roc/feature-utils';
import {
  CurrencyField,
  DateField,
  FieldLabel,
  GoogleMap,
  MultiSelectField,
  RadioField,
  SelectField,
  TextField,
  trueFalseOptions,
  yesNoOptions,
  NumberFormat,
} from '@roc/ui';
import {
  createAutocompleteAddressField,
  createSelectField,
  createTextField,
} from '../../../propertyBuilderTemp';
import { formatCurrency } from '@roc/ui/utils';
import { format, isValid } from 'date-fns';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { LoanSubType, isNil } from '@roc/feature-utils';
import { AutocompleteAddressField } from './autoCompleteAddress';

const propertySources = [
  { value: 'Short Sale', label: 'Short Sale' },
  { value: 'REO', label: 'REO' },
  { value: 'Wholesaler', label: 'Wholesaler' },
  { value: 'Foreclosure Auction', label: 'Foreclosure Auction' },
  { value: "Sheriff's Sale", label: "Sheriff's Sale" },
  { value: 'Estate Sale', label: 'Estate Sale' },
  { value: 'Online Auction', label: 'Online Auction' },
  { value: 'Bankruptcy Sale', label: 'Bankruptcy Sale' },
  { value: 'MLS', label: 'MLS' },
  { value: 'Pre-Foreclosure', label: 'Pre-Foreclosure' },
  { value: 'Entity to Entity', label: 'Entity to Entity' },
  { value: 'Inheritance', label: 'Inheritance' },
  { value: 'Private Sale', label: 'Private Sale' },
  { value: 'Other', label: 'Other' },
];

const wholesalerPropertySources = [
  { value: 'Short Sale', label: 'Short Sale' },
  { value: 'REO', label: 'REO' },
  { value: 'Foreclosure Auction', label: 'Foreclosure Auction' },
  { value: "Sheriff's Sale", label: "Sheriff's Sale" },
  { value: 'Estate Sale', label: 'Estate Sale' },
  { value: 'Online Auction', label: 'Online Auction' },
  { value: 'Bankruptcy Sale', label: 'Bankruptcy Sale' },
  { value: 'MLS', label: 'MLS' },
  { value: 'Pre-Foreclosure', label: 'Pre-Foreclosure' },
  { value: 'Entity to Entity', label: 'Entity to Entity' },
  { value: 'Inheritance', label: 'Inheritance' },
  { value: 'Private Sale', label: 'Private Sale' },
  { value: 'Other', label: 'Other' },
];

const wholesalerRelationships = [
  { value: 'Relative', label: 'Relative' },
  { value: 'Non-Relative', label: 'Non-Relative' },
];

const propertyOwnerships = [
  { label: 'Purchase', value: 'Purchase' },
  { label: 'Already Owns the Property', value: 'Owned By Borrower' },
];

const exitStrategies = [
  { value: 'Refinance', label: 'Refinance' },
  { value: 'Sale', label: 'Sale' },
];

const useAddressStyles = makeStyles((theme: Theme) => ({
  autocompleteContainer: {
    marginBottom: theme.spacing(2),
  },
  mapStyles: {
    position: 'relative',
    width: '100%',
    height: '150px',
  },
}));

const stateOptions = statesAbbreviation.map((abbreviation, i) => ({
  value: abbreviation,
  label: statesNames[i],
}));

const addressLine2Tooltip =
  'Address Line 2 can be used for additional information\nsuch as apartment number, suite number, unit number\nor others details that can help in identifying the\nexact location of the address.';

const STREET_NUMBER_REGEX = /^(\d+)\s(.*)$/;

interface PropertyAddressDetailsProps {
  testId?: string;
  store: any;
  disabled?: boolean;
  singlePropertyQuoteStore?: any;
  standaloneLabel?: boolean;
  showMap?: boolean;
  autoFocus?: boolean;
  addMarkForRequiredFields?: boolean;
}

export const PropertyAddressDetails = observer(
  ({
    testId = 'address',
    store,
    disabled = false,
    standaloneLabel = true,
    showMap = true,
    autoFocus = false,
    addMarkForRequiredFields = true,
  }: PropertyAddressDetailsProps) => {
    const classes = useAddressStyles();

    const formFields = store?.form?.fields;

    const handleAddressChange = event => {
      const { value, fullInfo } = event?.target || {};
      const { address_components, geometry } = fullInfo || {};
      if (!isNil(address_components) && !isNil(geometry)) {
        store.saveAddressField(value, address_components, geometry);
      } else {
        const address = event.target.value;
        store.onFieldChange('address', address);
        if (STREET_NUMBER_REGEX.test(address)) {
          const [, streetNumber, streetName] = address.match(
            STREET_NUMBER_REGEX
          );
          store.onFieldChange('streetName', streetName);
          store.onFieldChange('streetNumber', streetNumber);
        } else {
          store.onFieldChange('streetName', address);
          store.onFieldChange('streetNumber', '');
        }
      }
    };

    const stateOptionsFiltered = [...stateOptions];
    stateOptionsFiltered.shift();

    const showGoogleMap =
      showMap &&
      Boolean(formFields?.latitude?.value && formFields?.longitude?.value);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <AutocompleteAddressField
            label="Address Line 1"
            disabled={disabled}
            required={true}
            name="address"
            value={formFields?.address?.value}
            error={
              isNotBlank(formFields?.address?.error) ||
              isNotBlank(formFields.__isAddressInCorrectFormat__.error)
            }
            errorText={
              formFields?.address?.error ||
              formFields?.__isAddressInCorrectFormat__?.error
            }
            onChange={handleAddressChange}
            fullWidth
            testId={`address-${testId}`}
            standaloneLabel={standaloneLabel}
            autoFocus={autoFocus}
            addMarkForRequiredFields={addMarkForRequiredFields}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {createTextField({
            label: 'Address Line 2',
            fieldName: 'aptNumber',
            testId: `aptNumber-${testId}`,
            store: store,
            disabled,
            tooltip: addressLine2Tooltip,
            standaloneLabel,
            addMarkForRequiredFields,
          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createTextField({
            label: 'City',
            fieldName: 'city',
            testId: `city-${testId}`,
            store: store,
            disabled,
            standaloneLabel,
            addMarkForRequiredFields,
          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createSelectField({
            store: store,
            testId: `state-${testId}`,
            fieldName: 'state',
            label: 'State',
            options: stateOptionsFiltered,
            disabled,
            standaloneLabel,
            addMarkForRequiredFields,
          })}
        </Grid>
        <Grid item xs={12} sm={4}>
          {createTextField({
            label: 'Zip Code',
            fieldName: 'zipCode',
            testId: `zipCode-${testId}`,
            store: store,
            disabled,
            standaloneLabel,
            addMarkForRequiredFields,
          })}
        </Grid>
        {showGoogleMap && (
          <Grid item xs={12}>
            <GoogleMap
              mapStyles={classes.mapStyles}
              latitude={formFields?.latitude?.value}
              longitude={formFields?.longitude?.value}
            />
          </Grid>
        )}
      </Grid>
    );
  }
);

export const PropertyAddress = observer(
  ({ store, showGoogleMap = true, ...rest }) => {
    const classes = useAddressStyles();
    const formFields = store?.form?.fields;

    const handleAddressChange = (event, name) => {
      const { value, fullInfo } = event?.target || {};
      const { address_components, geometry } = fullInfo || {};
      store.saveAddressField(value, address_components, geometry);
    };
    return (
      <>
        <Box className={classes.autocompleteContainer}>
          <AutocompleteAddressField
            value={formFields?.address?.value}
            name="address"
            label="Enter property location"
            error={
              isNotBlank(formFields?.address?.error) ||
              isNotBlank(formFields?.__isAddressInCorrectFormat__?.error)
            }
            errorText={
              formFields?.address?.error ||
              formFields?.__isAddressInCorrectFormat__?.error
            }
            onChange={event => handleAddressChange(event, 'address')}
            fullWidth
            testId="address"
            {...rest}
          />
        </Box>
        {showGoogleMap &&
          formFields?.latitude?.value &&
          formFields?.longitude?.value ? (
          <GoogleMap
            mapStyles={classes.mapStyles}
            latitude={formFields?.latitude.value}
            longitude={formFields?.longitude.value}
          />
        ) : null}
      </>
    );
  }
);



export const PropertyOwnership = observer(
  ({ store, onChange = null, loanSubType = null, ...rest }) => {
    const formFields = store.form.fields;

    const handleChange = value => {
      store.onFieldChange('propertyOwnership', value);
      onChange && onChange('propertyOwnership', value);
    };

    return (
      <RadioField
        standaloneLabel
        label="Purchase or Refinance"
        value={formFields.propertyOwnership.value}
        errorText={formFields.propertyOwnership.error}
        error={isNotBlank(formFields.propertyOwnership.error)}
        name={'propertyOwnership'}
        options={propertyOwnerships}
        onChange={handleChange}
        fullWidth
        required
        row
        testId="propertyOwnership"
        {...rest}
      />
    );
  }
);

export const PurchasePrice = observer(({ store, ...rest }) => {
  const formFields = store.form.fields;
  return (
    <CurrencyField
      variant="outlined"
      standaloneLabel
      label="What was the Purchase Price of the Property?"
      value={formFields.purchasePrice.value}
      errorText={formFields.purchasePrice.error}
      error={isNotBlank(formFields.purchasePrice.error)}
      onChange={(name, value) =>
        store.onFieldChange('purchasePrice', Number(value))
      }
      fullWidth
      required
      testId="purchasePrice"
      decimalScale={0}
      {...rest}
    />
  );
});


export const PayoffAmount = observer(({ store, ...rest }) => {
  const formFields = store.form.fields;
  return (
    <CurrencyField
      variant="outlined"
      standaloneLabel
      label="What is the Payoff Amount of the Property?"
      value={formFields.payoffAmount.value}
      errorText={formFields.payoffAmount.error}
      error={isNotBlank(formFields.payoffAmount.error)}
      onChange={(name, value) =>
        store.onFieldChange('payoffAmount', Number(value))
      }
      fullWidth
      required
      testId="payoffAmount"
      {...rest}
    />
  );
});




export const PurchaseDate = observer(({ store, ...rest }) => {
  const formFields = store.form.fields;

  const manageDate = date => {
    let formattedDate = null;
    if (isValid(date)) {
      formattedDate = format(date, 'MM/dd/yyyy');
    }
    store.onFieldChange('purchaseDate', formattedDate);
  };

  return (
    <DateField
      inputVariant="outlined"
      testId="purchaseDate"
      format="MM/dd/yyyy"
      standaloneLabel
      label="When did you purchase the property?"
      value={formFields.purchaseDate.value}
      error={isNotBlank(formFields.purchaseDate.error)}
      helperText={formFields.purchaseDate.error}
      onChange={date => manageDate(date)}
      disableFuture={true}
      fullWidth
      required
      {...rest}
    />
  );
});



export const MonthlyMarketRent = observer(({ store }) => {
  const { form } = store;
  return (
    <CurrencyField
      variant="outlined"
      standaloneLabel
      label="Monthly Market Rent"
      value={form.fields.monthlyMarketRentProvidedAtOrigination.value}
      onChange={(name, value) =>
        store.onFieldChange(
          'monthlyMarketRentProvidedAtOrigination',
          Number(value)
        )
      }
      fullWidth
      testId="monthlyMarketRentProvidedAtOrigination"
      required
      error={isNotBlank(
        form.fields.monthlyMarketRentProvidedAtOrigination.error
      )}
      errorText={form.fields.monthlyMarketRentProvidedAtOrigination.error}
    />
  );
});

export const AnnualTaxes = observer(({ store }) => {
  const { form } = store;

  return (
    <CurrencyField
      variant="outlined"
      standaloneLabel
      label="Annual Taxes"
      value={form.fields.annualTaxesProvidedAtOrigination.value}
      onChange={(name, value) =>
        store.onFieldChange('annualTaxesProvidedAtOrigination', Number(value))
      }
      fullWidth
      testId="annualTaxesProvidedAtOrigination"
      required
      error={isNotBlank(form.fields.annualTaxesProvidedAtOrigination.error)}
      errorText={form.fields.annualTaxesProvidedAtOrigination.error}
    />
  );
});

export const AnnualInsurance = observer(({ store }) => {
  const { form } = store;
  return (
    <CurrencyField
      variant="outlined"
      standaloneLabel
      label="Annual Insurance"
      value={form.fields.annualInsuranceProvidedAtOrigination.value}
      onChange={(name, value) =>
        store.onFieldChange(
          'annualInsuranceProvidedAtOrigination',
          Number(value)
        )
      }
      fullWidth
      testId="annualInsuranceProvidedAtOrigination"
      required
      error={isNotBlank(form.fields.annualInsuranceProvidedAtOrigination.error)}
      errorText={form.fields.annualInsuranceProvidedAtOrigination.error}
    />
  );
});

export const AnnualHoa = observer(({ store }) => {
  const { form } = store;
  return (
    <CurrencyField
      variant="outlined"
      standaloneLabel
      label="Annual HOA Dues"
      value={form.fields.annualHOAProvidedAtOrigination.value}
      onChange={(name, value) =>
        store.onFieldChange('annualHOAProvidedAtOrigination', Number(value))
      }
      fullWidth
      testId="annualHOAProvidedAtOrigination"
      required
      error={isNotBlank(form.fields.annualHOAProvidedAtOrigination.error)}
      errorText={form.fields.annualHOAProvidedAtOrigination.error}
    />
  );
});
